/**
 * Tool store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { toolsApi, toolRunsApi, toolCategoriesApi, toolTagsApi, toolStudiosApi } from '@/lib/api/tools';

const useToolStore = create((set, get) => ({
  // Catalog state — keyed by slug for O(1) lookup
  toolsBySlug: {},
  // Ordered slug list of the most recent /json/tools response (for the browse page)
  catalogSlugs: [],
  isLoadingCatalog: false,
  isLoadingMoreCatalog: false,
  catalogError: null,
  isFetchedCatalog: false,
  catalogFilter: { category: null, tag: null, q: '' },
  // Server-side pagination state. `catalogActiveFilters` is the exact filter
  // bag last sent to `/json/tools` so `loadMoreCatalog()` can ask for the
  // next page under the same filters (search query, category, etc.).
  // `catalogTotal` is the real DB count (from the backend's
  // `pagination.total`), not just the loaded slice — drives the count badge
  // in the page header and the "Load more" button visibility.
  catalogActiveFilters: {},
  catalogPageSize: 60,
  catalogTotal: 0,
  catalogHasMore: false,
  // In-flight dedup: several components mount in parallel on a fresh page
  // load (sidebar, browse page, chat suggestions, etc.) and used to fire
  // 2–4 identical `/json/tools` requests before any flag flipped. The first
  // caller stores its promise here; later callers with the same filter
  // signature piggyback on it instead of issuing another request.
  _inFlightCatalogPromise: null,
  _inFlightCatalogSig:     null,

  // Featured tools (sidebar collapsible)
  featuredSlugs: [],
  isLoadingFeatured: false,
  isFetchedFeatured: false,
  _inFlightFeaturedPromise: null,

  // Single-tool detail cache (key = slug)
  detailLoadingBySlug: {},
  detailErrorBySlug: {},

  // Run history (keyed by tool slug; empty key = all-tools history)
  runsByToolSlug: { __all__: [] },
  isLoadingRunsByToolSlug: {},
  runsErrorByToolSlug: {},

  // In-flight execution state per tool slug
  isRunningByToolSlug: {},
  runErrorByToolSlug: {},

  // Cross-component bridge between the tool-detail page and external triggers
  // (e.g. the navigation header dropdowns). These slots are written by
  // requesters and consumed by the page via useEffect. The `_v` (version)
  // counter ensures repeated requests with the same payload still re-fire.
  pendingExampleByToolSlug:     {}, // { [slug]: { example, index, _v } }
  pendingActiveRunIdByToolSlug: {}, // { [slug]: { runId, _v } }

  // Taxonomy — categories / tags / studios for the browse + filter UI
  categories: [],
  isLoadingCategories: false,
  isFetchedCategories: false,
  categoriesError: null,

  tags: [],
  isLoadingTags: false,
  isFetchedTags: false,
  tagsError: null,

  studios: [],
  isLoadingStudios: false,
  isFetchedStudios: false,
  studiosError: null,

  // ----- helpers ---------------------------------------------------------
  getToolBySlug: (slug) => get().toolsBySlug[slug] || null,

  // Cache: model slug → tool slug (or `null` if the server confirmed there
  // isn't a dedicated tool for that model). Used by surfaces that show
  // "the tool for this specific model" (e.g. ModelDetailsModal) so we
  // don't refetch on every modal open.
  toolSlugByModelSlug: {},

  // Returns the tool currently cached as "the" tool for the given model
  // slug, or null. Does NOT trigger a network request — pair with
  // `fetchToolForModel` when you need to populate the cache.
  getToolByModelSlug: (modelSlug) => {
    if (!modelSlug) return null;
    const toolSlug = get().toolSlugByModelSlug[modelSlug];
    if (!toolSlug) return null;
    return get().toolsBySlug[toolSlug] || null;
  },

  // Resolves the tool that's pinned to `modelSlug` via `defaultModel`.
  // First serves from cache, then scans `toolsBySlug` (so already-fetched
  // tools resolve without a network call), and finally hits
  // `/json/tools?defaultModel=<slug>&limit=1`. Caches a `null` result
  // when the server confirms the model has no dedicated tool, so we
  // don't keep refetching empty answers.
  fetchToolForModel: async (modelSlug) => {
    if (!modelSlug) return null;
    const cached = get().toolSlugByModelSlug[modelSlug];
    if (cached !== undefined) {
      return cached ? (get().toolsBySlug[cached] || null) : null;
    }
    const inMemory = Object.values(get().toolsBySlug).find(t => t?.defaultModel?.slug === modelSlug);
    if (inMemory) {
      set(state => ({
        toolSlugByModelSlug: { ...state.toolSlugByModelSlug, [modelSlug]: inMemory.slug },
      }));
      return inMemory;
    }
    try {
      const data = await toolsApi.list({ defaultModel: modelSlug, limit: 1 });
      const tool = (data.tools || [])[0] || null;
      if (tool) get().upsertTools([tool]);
      set(state => ({
        toolSlugByModelSlug: { ...state.toolSlugByModelSlug, [modelSlug]: tool ? tool.slug : null },
      }));
      return tool;
    } catch (_err) {
      // Modal is non-critical UI — fail silent and let the section just
      // not render. Don't poison the cache so a later open can retry.
      return null;
    }
  },

  upsertTools: (tools) => {
    if (!Array.isArray(tools) || !tools.length) return;
    set(state => ({
      toolsBySlug: {
        ...state.toolsBySlug,
        ...Object.fromEntries(tools.filter(Boolean).map(t => [t.slug, { ...(state.toolsBySlug[t.slug] || {}), ...t }])),
      },
    }));
  },

  setCatalogFilter: (partial) => {
    set(state => ({ catalogFilter: { ...state.catalogFilter, ...partial } }));
  },

  // ----- catalog ---------------------------------------------------------
  //
  // `fetchCatalog(filters)` always resets pagination — it represents a "new
  // browse session" (search changed, category changed, refresh, etc.). The
  // filter bag is captured into `catalogActiveFilters` so the follow-up
  // `loadMoreCatalog()` calls inherit the same `q` / `category` / `tag`.
  fetchCatalog: async (filters = {}) => {
    // Drop empty filter values so backend doesn't get `category=undefined`
    // strings in the query string.
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    // Piggyback on an already-running request for the same filters instead
    // of firing a duplicate (several callers mount in parallel on a fresh
    // page load). Filter signature is a stable JSON string.
    const sig = JSON.stringify(cleanFilters);
    const inFlight = get()._inFlightCatalogPromise;
    if (inFlight && get()._inFlightCatalogSig === sig) return inFlight;

    const promise = (async () => {
      set({ isLoadingCatalog: true, catalogError: null });
      try {
        const pageSize = get().catalogPageSize;
        const params = { limit: pageSize, skip: 0, ...cleanFilters };
        const data = await toolsApi.list(params);
        const tools = data.tools || [];
        const pagination = data.pagination || {};
        get().upsertTools(tools);
        set({
          catalogSlugs: tools.map(t => t.slug),
          catalogTotal: pagination.total ?? tools.length,
          catalogHasMore: pagination.hasMore ?? false,
          catalogActiveFilters: cleanFilters,
          isFetchedCatalog: true,
          catalogError: null,
        });
        return tools;
      } catch (error) {
        set({ catalogError: error.message || 'Failed to load tools' });
        throw error;
      } finally {
        set({ isLoadingCatalog: false, _inFlightCatalogPromise: null, _inFlightCatalogSig: null });
      }
    })();
    set({ _inFlightCatalogPromise: promise, _inFlightCatalogSig: sig });
    return promise;
  },

  // Paginate forward under the filters last passed to `fetchCatalog`. Safe
  // to call repeatedly — no-ops while a load is in flight or once the
  // server has reported `hasMore: false`. New slugs are deduped before
  // append so a race between two clicks can't double-insert.
  loadMoreCatalog: async () => {
    const state = get();
    if (state.isLoadingMoreCatalog || state.isLoadingCatalog) return;
    if (!state.catalogHasMore) return;
    set({ isLoadingMoreCatalog: true });
    try {
      const params = {
        limit: state.catalogPageSize,
        skip:  state.catalogSlugs.length,
        ...state.catalogActiveFilters,
      };
      const data = await toolsApi.list(params);
      const tools = data.tools || [];
      const pagination = data.pagination || {};
      get().upsertTools(tools);
      const existing = new Set(state.catalogSlugs);
      const additions = tools.map(t => t.slug).filter(s => !existing.has(s));
      set({
        catalogSlugs: [...state.catalogSlugs, ...additions],
        catalogTotal: pagination.total ?? state.catalogTotal,
        catalogHasMore: pagination.hasMore ?? false,
      });
    } catch (error) {
      // Load-more failures are non-fatal — leave the catalog as-is and let
      // the user re-trigger by clicking again. We surface the error via
      // `catalogError` but keep `isFetchedCatalog: true` so the UI stays
      // populated with the slugs we already have.
      set({ catalogError: error.message || 'Failed to load more tools' });
    } finally {
      set({ isLoadingMoreCatalog: false });
    }
  },

  fetchFeatured: async (limit = 8) => {
    // Piggyback on an already-running request instead of firing a duplicate
    // (sidebar + app shell + browse page can all mount together).
    const inFlight = get()._inFlightFeaturedPromise;
    if (inFlight) return inFlight;

    const promise = (async () => {
      set({ isLoadingFeatured: true });
      try {
        const data = await toolsApi.list({ featured: true, limit });
        const tools = data.tools || [];
        get().upsertTools(tools);
        set({ featuredSlugs: tools.map(t => t.slug), isFetchedFeatured: true });
      } catch (e) {
        // sidebar is non-critical; swallow
        set({ isFetchedFeatured: true });
      } finally {
        set({ isLoadingFeatured: false, _inFlightFeaturedPromise: null });
      }
    })();
    set({ _inFlightFeaturedPromise: promise });
    return promise;
  },

  // ----- single tool detail ---------------------------------------------
  fetchToolBySlug: async (slug, { force = false } = {}) => {
    if (!slug) return null;
    if (!force && get().toolsBySlug[slug]?.fields) {
      // already have the full detail (catalog responses might omit fields)
      return get().toolsBySlug[slug];
    }
    set(state => ({
      detailLoadingBySlug: { ...state.detailLoadingBySlug, [slug]: true },
      detailErrorBySlug: { ...state.detailErrorBySlug, [slug]: null },
    }));
    try {
      const data = await toolsApi.getBySlug(slug);
      const tool = data.tool;
      if (tool) get().upsertTools([tool]);
      return tool;
    } catch (error) {
      set(state => ({
        detailErrorBySlug: { ...state.detailErrorBySlug, [slug]: error.message || 'Failed to load tool' },
      }));
      throw error;
    } finally {
      set(state => ({
        detailLoadingBySlug: { ...state.detailLoadingBySlug, [slug]: false },
      }));
    }
  },

  // ----- run -------------------------------------------------------------
  runTool: async (slug, payload = {}) => {
    set(state => ({
      isRunningByToolSlug: { ...state.isRunningByToolSlug, [slug]: true },
      runErrorByToolSlug:  { ...state.runErrorByToolSlug,  [slug]: null  },
    }));
    try {
      const data = await toolsApi.run(slug, payload);
      const run = data.toolRun;
      // Prepend to local cached history for snappy UX
      if (run) {
        set(state => {
          const list = state.runsByToolSlug[slug] || [];
          const all  = state.runsByToolSlug.__all__ || [];
          return {
            runsByToolSlug: {
              ...state.runsByToolSlug,
              [slug]: [run, ...list.filter(r => r._id !== run._id)],
              __all__: [run, ...all.filter(r => r._id !== run._id)],
            },
          };
        });
      }
      return run;
    } catch (error) {
      set(state => ({
        runErrorByToolSlug: { ...state.runErrorByToolSlug, [slug]: error.message || 'Tool run failed' },
      }));
      throw error;
    } finally {
      set(state => ({
        isRunningByToolSlug: { ...state.isRunningByToolSlug, [slug]: false },
      }));
    }
  },

  // ----- run history -----------------------------------------------------
  fetchRuns: async ({ toolSlug = null, limit = 20 } = {}) => {
    const key = toolSlug || '__all__';
    set(state => ({
      isLoadingRunsByToolSlug: { ...state.isLoadingRunsByToolSlug, [key]: true },
      runsErrorByToolSlug:     { ...state.runsErrorByToolSlug,     [key]: null  },
    }));
    try {
      const data = await toolRunsApi.list({ ...(toolSlug ? { toolSlug } : {}), limit });
      const runs = data.toolRuns || [];
      set(state => ({
        runsByToolSlug: { ...state.runsByToolSlug, [key]: runs },
      }));
      return runs;
    } catch (error) {
      set(state => ({
        runsErrorByToolSlug: { ...state.runsErrorByToolSlug, [key]: error.message || 'Failed to load history' },
      }));
      throw error;
    } finally {
      set(state => ({
        isLoadingRunsByToolSlug: { ...state.isLoadingRunsByToolSlug, [key]: false },
      }));
    }
  },

  // Promote a completed tool run to a fresh, user-visible Conversation.
  promoteRunToConversation: async (runId) => {
    const data = await toolRunsApi.promoteToConversation(runId);
    return data.conversation; // { id, title }
  },

  // ----- cross-component bridge ----------------------------------------
  // Used by the navigation header dropdowns to drive the tool-detail page
  // (apply a preset to the form, switch the active run). The page reads
  // these slots in a useEffect and clears them after consuming.
  requestApplyExample: (slug, example, index) => {
    if (!slug) return;
    set(state => ({
      pendingExampleByToolSlug: {
        ...state.pendingExampleByToolSlug,
        [slug]: { example, index, _v: Date.now() },
      },
    }));
  },
  clearPendingExample: (slug) => {
    if (!slug) return;
    set(state => {
      if (!state.pendingExampleByToolSlug[slug]) return {};
      const next = { ...state.pendingExampleByToolSlug };
      delete next[slug];
      return { pendingExampleByToolSlug: next };
    });
  },
  requestActiveRun: (slug, runId) => {
    if (!slug) return;
    set(state => ({
      pendingActiveRunIdByToolSlug: {
        ...state.pendingActiveRunIdByToolSlug,
        [slug]: { runId, _v: Date.now() },
      },
    }));
  },
  clearPendingActiveRun: (slug) => {
    if (!slug) return;
    set(state => {
      if (!state.pendingActiveRunIdByToolSlug[slug]) return {};
      const next = { ...state.pendingActiveRunIdByToolSlug };
      delete next[slug];
      return { pendingActiveRunIdByToolSlug: next };
    });
  },

  // Soft-delete a run. Optimistically removes it from every cached list.
  deleteRun: async (runId) => {
    if (!runId) return;
    set(state => {
      const next = {};
      for (const [key, list] of Object.entries(state.runsByToolSlug)) {
        next[key] = (list || []).filter(r => r.id !== runId && r._id !== runId);
      }
      return { runsByToolSlug: next };
    });
    try {
      await toolRunsApi.delete(runId);
    } catch (e) {
      // Re-fetch the global list to recover from any inconsistency.
      get().fetchRuns({}).catch(() => {});
      throw e;
    }
  },

  // ----- taxonomy --------------------------------------------------------
  fetchCategories: async ({ force = false } = {}) => {
    if (!force && (get().isFetchedCategories || get().isLoadingCategories)) return get().categories;
    set({ isLoadingCategories: true, categoriesError: null });
    try {
      const data = await toolCategoriesApi.list();
      const categories = data.toolCategories || [];
      set({ categories, isFetchedCategories: true });
      return categories;
    } catch (error) {
      set({ categoriesError: error.message || 'Failed to load tool categories' });
      throw error;
    } finally {
      set({ isLoadingCategories: false });
    }
  },

  fetchTags: async ({ force = false, featured = false } = {}) => {
    if (!force && (get().isFetchedTags || get().isLoadingTags)) return get().tags;
    set({ isLoadingTags: true, tagsError: null });
    try {
      const data = await toolTagsApi.list({ featured });
      const tags = data.toolTags || [];
      set({ tags, isFetchedTags: true });
      return tags;
    } catch (error) {
      set({ tagsError: error.message || 'Failed to load tool tags' });
      throw error;
    } finally {
      set({ isLoadingTags: false });
    }
  },

  fetchStudios: async ({ force = false } = {}) => {
    if (!force && (get().isFetchedStudios || get().isLoadingStudios)) return get().studios;
    set({ isLoadingStudios: true, studiosError: null });
    try {
      const data = await toolStudiosApi.list();
      const studios = data.toolStudios || [];
      set({ studios, isFetchedStudios: true });
      return studios;
    } catch (error) {
      set({ studiosError: error.message || 'Failed to load tool studios' });
      throw error;
    } finally {
      set({ isLoadingStudios: false });
    }
  },

  // Reset (used on logout)
  reset: () => set({
    toolsBySlug: {},
    catalogSlugs: [],
    isLoadingCatalog: false,
    catalogError: null,
    isFetchedCatalog: false,
    catalogFilter: { category: null, tag: null, q: '' },
    featuredSlugs: [],
    isLoadingFeatured: false,
    isFetchedFeatured: false,
    detailLoadingBySlug: {},
    detailErrorBySlug: {},
    runsByToolSlug: { __all__: [] },
    isLoadingRunsByToolSlug: {},
    runsErrorByToolSlug: {},
    isRunningByToolSlug: {},
    runErrorByToolSlug: {},
    categories: [],
    isLoadingCategories: false,
    isFetchedCategories: false,
    categoriesError: null,
    tags: [],
    isLoadingTags: false,
    isFetchedTags: false,
    tagsError: null,
    studios: [],
    isLoadingStudios: false,
    isFetchedStudios: false,
    studiosError: null,
  }),
}));

export { useToolStore };
export default useToolStore;
